import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import { PriceDotIndicator } from "@apac/components/ui";
import { usePassengerCountString } from "@apac/hooks";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import {
  type IPassengerCounts,
  type ITripTerminus,
  TripCategory,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { noop } from "@hopper-b2b/utilities";
import {
  CloseButton,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Icon,
} from "@tenant/ui";
import styles from "./CalendarDialogHeader.module.scss";

export const CalendarDialogHeader = ({
  tripCategory,
  origin,
  destination,
  passengerCounts,
}: {
  tripCategory: TripCategory;
  origin?: ITripTerminus;
  destination?: ITripTerminus;
  passengerCounts?: IPassengerCounts;
}) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "CalendarDialogHeader");

  const navigate = useNavigate();
  const location = useLocation();

  const onClose = useCallback(() => {
    navigate(`${location.pathname}${location.search}`, { replace: true });
  }, [location, navigate]);

  const passengerCountString = usePassengerCountString(
    undefined,
    passengerCounts
  );

  const title =
    tripCategory === TripCategory.ONE_WAY
      ? t("core-flights.search.dateSelection.title.short.oneWay")
      : t("core-flights.search.dateSelection.title.short.roundTrip");

  const bucketDescriptions = [
    t("priceCalendar.bucket1"),
    t("priceCalendar.bucket2"),
    t("priceCalendar.bucket3"),
    t("priceCalendar.bucket4"),
  ];

  return (
    <div className={block}>
      <CloseButton
        label={t("back")}
        className={cn("back-button")}
        onClose={onClose ?? noop}
      >
        <Icon className={cn("back-icon")} iconName={IconNameEnum.backIcon} />
      </CloseButton>
      <div className={cn("title-container")}>
        <div className={cn("title")}>{title}</div>
        <div className={cn("location")}>
          <span className={cn("location-origin")}>{origin?.label}</span>
          <Icon
            className={cn("airplane-icon")}
            iconName={IconNameEnum.airplane}
          />
          <span className={cn("location-destination")}>
            {destination?.label}
          </span>
        </div>
        <div className={cn("passenger")}>{`${passengerCountString}`}</div>
      </div>
      <Dialog>
        <DialogTrigger className={cn("trigger-button")}>
          <Icon className={cn("info-icon")} iconName={IconNameEnum.info} />
        </DialogTrigger>

        <DialogContent
          className={cn("bucket-info-dialog-content")}
          title={t("priceCalendar.title")}
          description={t("priceCalendar.subtitle")}
        >
          <div className={cn("bucket-info")}>
            {bucketDescriptions.map((description, index) => (
              <div className={cn("bucket-info-item")}>
                <PriceDotIndicator
                  priceBucket={index}
                  className={cn("price-dot-indicator")}
                />
                <span className={cn("bucket-info-item-description")}>
                  {description}
                </span>
              </div>
            ))}
          </div>
          <DialogClose className={cn("close-button")}>
            {t("close.button")}
          </DialogClose>
        </DialogContent>
      </Dialog>
    </div>
  );
};
